import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { FlaskConical, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/flashcards"), 2500);
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-sm ss-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <FlaskConical className="h-7 w-7 text-slate-900" />
          <span className="font-display font-bold text-xl text-slate-900">Science Sorted</span>
        </div>
        {done ? (
          <div data-testid="reset-success" className="text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Password updated</h1>
            <p className="mt-2 text-slate-500">Redirecting you to login…</p>
          </div>
        ) : !token ? (
          <p className="text-slate-600">This reset link is invalid. Please request a new one.</p>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900">Set a new password</h1>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  required data-testid="reset-password-input" className="mt-1.5 bg-white" />
              </div>
              <div>
                <Label htmlFor="cpw">Confirm password</Label>
                <Input id="cpw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  required data-testid="reset-confirm-input" className="mt-1.5 bg-white" />
              </div>
              {error && <p className="text-sm text-rose-600" data-testid="reset-error">{error}</p>}
              <Button type="submit" disabled={busy} data-testid="reset-submit-button"
                className="w-full bg-slate-900 hover:bg-slate-800 h-11">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </>
        )}
        <div className="mt-6">
          <Link to="/flashcards" className="text-sm text-slate-500 hover:text-slate-900">Back to login</Link>
        </div>
      </div>
    </div>
  );
}
